import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Repeat, CalendarClock, Trash2, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

/** Recurring auto-deposit configuration card.
 * PIX doesn't support silent debit; the backend loop creates a fresh Pix QR
 * on each scheduled date and pushes a notification to the user. */
export default function AutoDepositCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [amount, setAmount] = useState("50");
  const [frequency, setFrequency] = useState("monthly");
  const [active, setActive] = useState(true);
  const [lowBalanceOn, setLowBalanceOn] = useState(false);
  const [minBalance, setMinBalance] = useState("10");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/wallet/auto-deposit`);
      if (r.data && r.data.user_id) {
        setSchedule(r.data);
        setAmount(String(r.data.amount || 50));
        setFrequency(r.data.frequency || "monthly");
        setActive(!!r.data.active);
        const mb = Number(r.data.min_balance || 0);
        setLowBalanceOn(mb > 0);
        if (mb > 0) setMinBalance(String(mb));
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const val = parseFloat(amount);
    if (Number.isNaN(val) || val < 5 || val > 10000) {
      toast.error("Valor deve estar entre R$ 5 e R$ 10.000");
      return;
    }
    const mb = lowBalanceOn ? parseFloat(minBalance) : 0;
    if (lowBalanceOn && (Number.isNaN(mb) || mb < 0 || mb > 10000)) {
      toast.error("Saldo mínimo deve estar entre R$ 0 e R$ 10.000");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/wallet/auto-deposit`, {
        amount: val, frequency, active,
        min_balance: mb,
      });
      setSchedule(r.data);
      toast.success(active ? "Depósito automático ativado!" : "Preferências salvas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Cancelar o depósito automático?")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/wallet/auto-deposit`);
      setSchedule(null);
      toast.success("Depósito automático cancelado");
    } catch { toast.error("Falha ao cancelar"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Card className="p-4">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  return (
    <Card
      data-testid="auto-deposit-card"
      className="p-4 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/40">
            <Repeat className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-base tracking-tight">Depósito automático</h3>
            <div className="text-[11px] text-muted-foreground">Nunca fique sem saldo para impulsionar seus anúncios</div>
          </div>
        </div>
        <Switch
          checked={active}
          onCheckedChange={setActive}
          data-testid="auto-deposit-toggle"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <label className="text-[11px] font-medium text-muted-foreground">Valor (R$)</label>
          <Input
            type="number"
            min={5}
            max={10000}
            step={5}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-9"
            data-testid="auto-deposit-amount"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium text-muted-foreground">Frequência</label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            data-testid="auto-deposit-frequency"
          >
            <option value="weekly">Semanal</option>
            <option value="monthly">Mensal</option>
          </select>
        </div>
      </div>

      {schedule?.next_due_at && active && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300 mb-3 px-2 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/20">
          <CalendarClock className="w-3.5 h-3.5" />
          <span>
            Próximo Pix em <b>{fmtDate(schedule.next_due_at)}</b> — notificaremos com o QR quando estiver pronto
          </span>
        </div>
      )}

      {/* Low balance alert toggle */}
      <div
        className={`rounded-md border p-2.5 mb-3 transition-colors ${
          lowBalanceOn ? "border-orange-500/40 bg-orange-500/5" : "border-border bg-muted/30"
        }`}
        data-testid="low-balance-section"
      >
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={lowBalanceOn}
            onChange={(e) => setLowBalanceOn(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-input"
            data-testid="low-balance-toggle"
          />
          <div className="flex-1">
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
              Também disparar quando saldo cair abaixo de
            </div>
            {lowBalanceOn && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">R$</span>
                <Input
                  type="number"
                  min={0}
                  max={10000}
                  step={5}
                  value={minBalance}
                  onChange={(e) => setMinBalance(e.target.value)}
                  className="h-8 max-w-24"
                  data-testid="low-balance-amount"
                />
                <span className="text-[11px] text-muted-foreground">Pix disparado imediatamente (cooldown 1h)</span>
              </div>
            )}
            {!lowBalanceOn && (
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Perfeito para quem tem anúncios rodando: recarrega antes de pausar campanha
              </div>
            )}
          </div>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={save}
          disabled={saving}
          size="sm"
          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="auto-deposit-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
          {schedule ? "Atualizar" : "Ativar"}
        </Button>
        {schedule && (
          <Button
            onClick={remove}
            disabled={saving}
            variant="outline"
            size="sm"
            className="gap-1.5"
            data-testid="auto-deposit-delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Cancelar
          </Button>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground mt-2 leading-snug">
        Como funciona: na data marcada, geramos o Pix e enviamos uma notificação. Você escaneia,
        paga e o saldo cai automaticamente na sua carteira via webhook.
      </p>
    </Card>
  );
}
